import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useRef,
} from 'react'
import { ApolloProvider } from '@apollo/client/react'
import { toast } from 'ui'
import {
  CompleteRadarChallengeDocument,
  type EnterpriseSsoInfo,
  RevertToAccountingAccountDocument,
  SelfDocument,
  SigninDocument,
  TwoFactorMethod,
  ValidateEmailVerificationCodeDocument,
  VerifyTwoFactorSmsAuthCodeDocument,
} from '../operations-types'
import Sentry from './sentry'
import jwt_decode from 'jwt-decode'
import {
  apolloClient,
  onAuthTokenChanged,
  onSignedOut,
  registerClientAccessEndedHandler,
  registerSessionExpiredHandler,
} from './apolloClient'
import { useRfiModalStore } from '../components/layout/RfiModalStore'
import { discardRfiDrafts } from '../components/onboarding/rfi/rfiApplicationStore'
import {
  clearTwoFactorChallengeToken,
  readTwoFactorChallengeToken,
  storeTwoFactorChallengeToken,
} from './twoFactorChallengeToken'
import { CLIENTS_URL } from './urls'
import {
  accountForSessionEnding,
  recordLogin,
  recordSessionEnding,
  type SessionEndingCause,
} from './refusedSession'

type AuthContext = {
  setAuth: (token: string) => void
  setAuthWhenReady: (token: string) => Promise<void>
  switchSession: (token: string, destination?: string) => void
  isSignedIn: () => boolean
  signIn: (params: {
    email: string
    password: string
    twoFactorMethod?: TwoFactorMethod
    signalsId?: string
  }) => Promise<
    | {
        error: string | null | undefined
        twoFactorRequired?: boolean
        twoFactorMethod?: string | null
        availableTwoFactorMethods?: TwoFactorMethod[] | null
        radarChallengeToken?: string | null
        enterpriseSso?: EnterpriseSsoInfo | null
      }
    | undefined
  >
  completeRadarChallenge: (params: {
    challengeToken: string
    code: string
  }) => Promise<
    | {
        error: { type: string | null; message: string | null } | null
        twoFactorRequired?: boolean
        twoFactorMethod?: string | null
        availableTwoFactorMethods?: TwoFactorMethod[] | null
      }
    | undefined
  >
  signOut: (cause?: SessionEndingCause) => void
  verifyTwoFactorCodeForLogin: (params: {
    identifier: string
    code: string
    method: 'EMAIL' | 'PHONE'
  }) => Promise<
    | {
        error: string | null | undefined
        expired?: boolean
      }
    | undefined
  >
  getToken: () => string | null
  getAccountantUserId: () => string | null
  getAdminUserId: () => string | null
}

const authContext = createContext<AuthContext | null>(null)

function isTokenExpired(token: string): boolean {
  try {
    const { expiry, exp }: { expiry?: number; exp?: number } = jwt_decode(token)

    const expiresAtMs = expiry ?? (exp !== undefined ? exp * 1000 : undefined)
    if (!expiresAtMs) {
      return true
    }
    return Date.now() > expiresAtMs
  } catch (error) {
    Sentry.captureException(
      `Error decoding login token: ${JSON.stringify(error)}`,
    )
    return true
  }
}

export function sessionIdentity(token: string | null): string | null {
  if (!token || isTokenExpired(token)) {
    return null
  }
  try {
    const { userId, accountantUserId, adminId } = jwt_decode<{
      userId?: string
      accountantUserId?: string
      adminId?: string
    }>(token)
    if (!userId) {
      return null
    }
    return [userId, accountantUserId ?? '', adminId ?? ''].join(':')
  } catch {
    return null
  }
}

export type SessionChange =
  | 'none'
  | 'refreshed'
  | 'signedIn'
  | 'signedOut'
  | 'switched'

export function sessionChange(
  held: string | null,
  stored: string | null,
): SessionChange {
  if (held === stored) {
    return 'none'
  }
  const from = sessionIdentity(held)
  const to = sessionIdentity(stored)
  if (from === to) {
    return from === null ? 'none' : 'refreshed'
  }
  if (to === null) {
    return 'signedOut'
  }
  if (from === null) {
    return 'signedIn'
  }
  return 'switched'
}

const SESSION_SWITCHED_ELSEWHERE_KEY = 'sessionSwitchedInAnotherTab'

function noteSessionSwitchedElsewhere() {
  try {
    sessionStorage.setItem(SESSION_SWITCHED_ELSEWHERE_KEY, 'true')
  } catch {
    // Session storage may be unavailable; the reload still happens.
  }
}

function sessionSwitchedElsewhereNoted(): boolean {
  try {
    return sessionStorage.getItem(SESSION_SWITCHED_ELSEWHERE_KEY) === 'true'
  } catch {
    return false
  }
}

function explainSessionSwitchedElsewhere() {
  try {
    sessionStorage.removeItem(SESSION_SWITCHED_ELSEWHERE_KEY)
  } catch {
    // Nothing to clear.
  }
  toast.info('You switched accounts in another tab', {
    id: SESSION_SWITCHED_ELSEWHERE_KEY,
    description:
      'This tab reloaded to show the same account. A browser stays signed in to one Nickel account at a time, so to work in two at once, use a separate browser profile.',
    duration: 15_000,
  })
}

const CLIENT_ACCESS_ENDED_KEY = 'clientAccessEnded'

function noteClientAccessEnded() {
  try {
    sessionStorage.setItem(CLIENT_ACCESS_ENDED_KEY, 'true')
  } catch {
    // Session storage may be unavailable; the switch still happens.
  }
}

function clientAccessEndedNoted(): boolean {
  try {
    return sessionStorage.getItem(CLIENT_ACCESS_ENDED_KEY) === 'true'
  } catch {
    return false
  }
}

function explainClientAccessEnded() {
  try {
    sessionStorage.removeItem(CLIENT_ACCESS_ENDED_KEY)
  } catch {
    // Nothing to clear.
  }
  // No client name, since the client may be gone, and no role.
  toast.info('Your access to that client has ended', {
    id: CLIENT_ACCESS_ENDED_KEY,
    description: 'You’re back in your firm’s account.',
    duration: 15_000,
  })
}

async function returnToFirm(
  auth: Pick<AuthContext, 'getAccountantUserId' | 'switchSession'>,
): Promise<boolean> {
  if (!auth.getAccountantUserId()) {
    return false
  }
  const result = await apolloClient
    .mutate({ mutation: RevertToAccountingAccountDocument })
    .catch(() => null)
  const token = result?.data?.revertToAccountingAccount?.token
  if (!token) {
    return false
  }
  noteClientAccessEnded()
  auth.switchSession(token, CLIENTS_URL)
  return true
}

function useExplainAfterReload(noted: () => boolean, explain: () => void) {
  useEffect(() => {
    if (!noted()) {
      return
    }
    if (document.visibilityState === 'visible') {
      explain()
      return
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisible)
        explain()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [noted, explain])
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useProvideAuth()
  const { signOut } = auth

  useEffect(() => {
    registerSessionExpiredHandler(() => signOut('session_expired'))
  }, [signOut])

  useEffect(() => {
    registerClientAccessEndedHandler(() => returnToFirm(auth))
  }, [auth])

  useExplainAfterReload(
    sessionSwitchedElsewhereNoted,
    explainSessionSwitchedElsewhere,
  )
  useExplainAfterReload(clientAccessEndedNoted, explainClientAccessEnded)

  return (
    <authContext.Provider value={auth}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </authContext.Provider>
  )
}

export const useAuth = (): AuthContext => {
  return useContext(authContext) as AuthContext
}

function useProvideAuth(): AuthContext {
  // Synchronous so `isSignedIn()` is right on first paint; otherwise an
  // auth-gated route navigates to /login, which then bounces to /dashboard
  // and drops the redirect param.
  const [authToken, setAuthToken] = useState<string | null>(() => {
    const token = localStorage.getItem('token')
    return token && !isTokenExpired(token) ? token : null
  })

  // Every write of the session state goes through `hold` so the ref, read by
  // the listeners below, never disagrees with it.
  const heldToken = useRef(authToken)
  const hold = useCallback((token: string | null) => {
    heldToken.current = token
    setAuthToken(token)
  }, [])

  useEffect(() => {
    const stored = localStorage.getItem('token')
    if (stored && isTokenExpired(stored) && !authToken) {
      signOut()
    }
  }, [])

  const isSignedIn = () => {
    if (authToken) {
      return true
    } else {
      return false
    }
  }

  const signIn = async ({
    email,
    password,
    twoFactorMethod,
    signalsId,
  }: {
    email: string
    password: string
    twoFactorMethod?: TwoFactorMethod
    signalsId?: string
  }) => {
    const result = await apolloClient.mutate({
      mutation: SigninDocument,
      variables: {
        email,
        password,
        twoFactorMethod,
        ...(signalsId ? { signalsId } : {}),
      },
    })

    if (result?.data?.login?.error) {
      return {
        error: result?.data?.login?.error.message,
      }
    }

    if (result?.data?.login?.radarChallengeToken) {
      return {
        error: null,
        radarChallengeToken: result.data.login.radarChallengeToken,
      }
    }

    if (result?.data?.login?.enterpriseSso) {
      return { error: null, enterpriseSso: result.data.login.enterpriseSso }
    }

    return settleLogin(result?.data?.login)
  }

  const settleLogin = async (
    login:
      | {
          token?: string | null
          twoFactorRequired?: boolean | null
          twoFactorMethod?: TwoFactorMethod | null
          twoFactorIdentifier?: string | null
          availableTwoFactorMethods?: TwoFactorMethod[] | null
          twoFactorChallengeToken?: string | null
        }
      | null
      | undefined,
  ) => {
    const availableTwoFactorMethods = login?.availableTwoFactorMethods
    if (
      login?.twoFactorRequired &&
      !login.twoFactorIdentifier &&
      (availableTwoFactorMethods?.length ?? 0) > 1
    ) {
      return {
        twoFactorRequired: true,
        availableTwoFactorMethods,
        error: null,
      }
    }

    if (login?.twoFactorRequired && login.twoFactorIdentifier) {
      localStorage.setItem('twoFactorIdentifier', login.twoFactorIdentifier)
      // A phone number is not unique across accounts, so the SMS code must be
      // checked against the account that just passed its password.
      storeTwoFactorChallengeToken(login.twoFactorChallengeToken)

      return {
        twoFactorRequired: true,
        twoFactorMethod: login.twoFactorMethod,
        error: null,
      }
    } else if (login?.token) {
      await setAuthWhenReady(login.token)
      try {
        sessionStorage.setItem('justLoggedIn', 'true')
      } catch {
        // Session storage may be unavailable in some contexts
      }

      return {
        error: null,
        twoFactorRequired: false,
        twoFactorMethod: null,
      }
    } else {
      Sentry.captureException(
        `No token returned from login: ${JSON.stringify(login)}`,
      )
    }
  }

  const completeRadarChallenge = async ({
    challengeToken,
    code,
  }: {
    challengeToken: string
    code: string
  }) => {
    const result = await apolloClient.mutate({
      mutation: CompleteRadarChallengeDocument,
      variables: { challengeToken, code },
    })

    const payload = result?.data?.completeRadarChallenge
    if (payload?.error) {
      return {
        error: {
          type: payload.error.type ?? null,
          message: payload.error.message ?? null,
        },
      }
    }
    return settleLogin(payload)
  }

  const verifyTwoFactorCodeForLogin = async ({
    identifier,
    code,
    method,
  }: {
    identifier: string
    code: string
    method: 'EMAIL' | 'PHONE'
  }) => {
    if (method === 'EMAIL') {
      const result = await apolloClient.mutate({
        mutation: ValidateEmailVerificationCodeDocument,
        variables: {
          email: identifier,
          code,
          login: true,
        },
      })

      if (result?.data?.validateEmailVerificationCode?.error) {
        return {
          error: result?.data?.validateEmailVerificationCode?.error.message,
        }
      }

      if (result?.data?.validateEmailVerificationCode?.data?.token) {
        await setAuthWhenReady(
          result.data.validateEmailVerificationCode.data.token,
        )
        try {
          sessionStorage.setItem('justLoggedIn', 'true')
        } catch {
          // Session storage may be unavailable in some contexts
        }
        localStorage.removeItem('email')
      } else {
        Sentry.captureException(
          `No token returned from login: ${JSON.stringify(result)}`,
        )
        return {
          error: 'An error occurred. Please try again.',
        }
      }
    } else if (method === 'PHONE') {
      const challengeToken = readTwoFactorChallengeToken()
      const result = await apolloClient.mutate({
        mutation: VerifyTwoFactorSmsAuthCodeDocument,
        variables: {
          identifier,
          code,
          ...(challengeToken ? { challengeToken } : {}),
        },
      })

      if (result?.data?.verifyTwoFactorSMSAuthCode?.error) {
        const { type, message } = result.data.verifyTwoFactorSMSAuthCode.error
        if (type === 'TWO_FACTOR_CHALLENGE_EXPIRED') {
          clearTwoFactorChallengeToken()
          localStorage.removeItem('twoFactorIdentifier')
          return { error: message, expired: true }
        }
        return { error: message }
      }

      if (result?.data?.verifyTwoFactorSMSAuthCode?.token) {
        await setAuthWhenReady(result.data.verifyTwoFactorSMSAuthCode.token)
        clearTwoFactorChallengeToken()
        try {
          sessionStorage.setItem('justLoggedIn', 'true')
        } catch {
          // Session storage may be unavailable in some contexts
        }
      } else {
        Sentry.captureException(
          `No token returned from login: ${JSON.stringify(result)}`,
        )
      }
    }
  }

  // Clears only this tab's state: a tab following another tab's sign-out must
  // not clear what a third tab, mid two-factor login, keeps in storage.
  const forgetSession = useCallback(
    (cause: SessionEndingCause) => {
      recordSessionEnding(heldToken.current, cause)
      hold(null)
      useRfiModalStore.getState().setShowPendingRfiModal(false)
      discardRfiDrafts()
      void onSignedOut()
    },
    [hold],
  )

  const signOut = useCallback(
    (cause: SessionEndingCause = 'sign_out') => {
      if (cause === 'sign_out') {
        accountForSessionEnding(localStorage.getItem('token'))
      }
      localStorage.removeItem('token')
      localStorage.removeItem('email')
      clearTwoFactorChallengeToken()
      forgetSession(cause)
    },
    [forgetSession],
  )

  const setAuth = (token: string) => {
    // Storage first: the auth link reads the token from storage per request.
    localStorage.setItem('token', token)
    hold(token)
    void onAuthTokenChanged()
  }

  const setAuthWhenReady = async (token: string) => {
    localStorage.setItem('token', token)
    // Held without the state change, so a visibilitychange while Self loads
    // does not read this tab's own token as another tab's sign-in.
    heldToken.current = token
    await onAuthTokenChanged()
    await apolloClient.query({ query: SelfDocument }).then(
      ({ data }) =>
        recordLogin(
          data?.user?.user?.organization?.id
            ? 'organization'
            : 'no_organization',
        ),
      (error: unknown) => recordLogin('rejected', String(error).slice(0, 200)),
    )
    if (localStorage.getItem('token') === token) {
      hold(token)
    }
  }

  /**
   * A switch of identity, done as a page load. setAuth's in-place swap cannot
   * be trusted: Apollo dedupes a refetch onto an identical in-flight operation
   * regardless of the Authorization header, so a read sent as the previous
   * identity answers it (staff becoming a user saw their own organization
   * until a manual reload). A page load also drops component state, the
   * analytics identity and stores outside Apollo.
   *
   * No React state changes here: a re-render before the load would refetch
   * Self on a route the new identity may not reach.
   */
  const switchSession = (token: string, destination?: string) => {
    localStorage.setItem('token', token)
    discardRfiDrafts()
    heldToken.current = token
    if (destination) {
      window.location.assign(destination)
    } else {
      // A filter or record id in the query belongs to the previous
      // organization, and a list page would remember it as the next one's
      // view (AGENTS.md § "Sticky List Filters"). Replacing the entry keeps
      // Back from loading that address as the new session.
      window.location.replace(window.location.pathname)
    }
  }

  // Follow the session when another tab changes it (one session per browser,
  // `sessionChange`). A switch to another identity reloads rather than
  // re-rendering in place, since this page's ids and forms belong to the
  // previous account. `pageshow` and `visibilitychange` catch a page restored
  // from bfcache or unfrozen, which may have missed the `storage` event.
  useEffect(() => {
    const follow = () => {
      const stored = localStorage.getItem('token')
      switch (sessionChange(heldToken.current, stored)) {
        case 'refreshed':
          hold(stored)
          return
        case 'signedIn':
          hold(stored)
          void onAuthTokenChanged()
          return
        case 'signedOut':
          forgetSession('signed_out_elsewhere')
          return
        case 'switched':
          noteSessionSwitchedElsewhere()
          discardRfiDrafts()
          window.location.assign('/dashboard')
          return
      }
    }
    const onStorage = (event: StorageEvent) => {
      // A null key is localStorage.clear() in another tab.
      if (event.key === 'token' || event.key === null) {
        follow()
      }
    }
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        follow()
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        follow()
      }
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('pageshow', onPageShow)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('pageshow', onPageShow)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [hold, forgetSession])

  const getToken = () => localStorage.getItem('token')

  const getAccountantUserId = () => {
    const token = getToken()

    if (!token) {
      return null
    }

    const {
      accountantUserId,
    }: {
      accountantUserId: string | undefined
    } = jwt_decode(token)

    return accountantUserId ?? null
  }

  const getAdminUserId = () => {
    const token = getToken()

    if (!token) {
      return null
    }

    const {
      adminId,
    }: {
      adminId: string | undefined
    } = jwt_decode(token)

    return adminId ?? null
  }

  return {
    setAuth,
    setAuthWhenReady,
    switchSession,
    isSignedIn,
    signIn,
    completeRadarChallenge,
    verifyTwoFactorCodeForLogin,
    signOut,
    getToken,
    getAccountantUserId,
    getAdminUserId,
  }
}
